import { cn } from '@/lib/cn';
import { resolveIcon } from './icons';
import { brandSlugOf, getBrand, isBrandIcon, isImageIcon, readableOn } from './brands';

interface CategoryIconProps {
  /** Nome lucide (kebab-case) OU marca ('brand:<slug>') do set curado. */
  icon?: string | null;
  /** Cor sólida do círculo (hex). */
  color: string;
  size?: number;
  /** Se não houver ícone resolvível, mostra este texto (ex.: inicial da conta). */
  fallbackText?: string;
  className?: string;
}

/**
 * Círculo sólido na cor + glifo lucide, LOGO de marca (bancos/bandeiras) ou
 * iniciais. Base visual de contas/categorias/cartões (DESIGN.md). A cor do glifo
 * se adapta à luminância do círculo (branco em fundo escuro, tinta em fundo claro).
 */
export function CategoryIcon({ icon, color, size = 40, fallbackText, className }: CategoryIconProps) {
  const glyphColor = readableOn(color);
  const glyph = Math.round(size * 0.5);

  const image = isImageIcon(icon) ? icon : undefined;
  const brand = isBrandIcon(icon) ? getBrand(brandSlugOf(icon)) : undefined;
  const brandImage = brand?.image;

  // Imagem personalizada ou logo de banco fornecido pelo usuário: fundo neutro +
  // object-contain para preservar o desenho original da marca.
  if (image || brandImage) {
    const source = image ?? brandImage!;
    return (
      <span
        className={cn('grid shrink-0 place-items-center overflow-hidden rounded-full bg-white', className)}
        style={{ width: size, height: size }}
        aria-hidden
      >
        <img src={source} alt="" className="size-full object-contain" />
      </span>
    );
  }

  return (
    <span
      className={cn('grid shrink-0 place-items-center overflow-hidden rounded-full', className)}
      style={{ width: size, height: size, backgroundColor: color, color: glyphColor }}
      aria-hidden
    >
      {brand ? (
        brand.path ? (
          <svg viewBox="0 0 24 24" width={glyph} height={glyph} fill="currentColor" role="presentation">
            <path d={brand.path} />
          </svg>
        ) : (
          <BrandMono text={brand.mono ?? brand.label} size={size} />
        )
      ) : icon ? (
        <LucideGlyph icon={icon} size={glyph} />
      ) : fallbackText ? (
        <span style={{ fontSize: Math.round(size * 0.42) }} className="font-semibold uppercase">
          {fallbackText.charAt(0)}
        </span>
      ) : (
        <LucideGlyph icon={null} size={glyph} />
      )}
    </span>
  );
}

function LucideGlyph({ icon, size }: { icon: string | null; size: number }) {
  const Icon = resolveIcon(icon);
  return <Icon style={{ width: size, height: size }} strokeWidth={2.2} />;
}

/** Iniciais da marca ajustadas ao comprimento (1–4 chars) para caber no círculo. */
function BrandMono({ text, size }: { text: string; size: number }) {
  const len = text.length;
  const factor = len <= 1 ? 0.46 : len === 2 ? 0.4 : len === 3 ? 0.32 : 0.26;
  return (
    <span
      style={{ fontSize: Math.round(size * factor), letterSpacing: len > 2 ? '-0.02em' : undefined }}
      className="font-bold leading-none"
    >
      {text}
    </span>
  );
}
